/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Companion World API
 * API for managing virtual worlds populated with AI-driven agents.

## Overview
The Companion World API provides endpoints for:
- Creating and managing virtual worlds
- Generating populations of AI agents
- Managing agent activities and calendars
- Configuring locations and demographics

## Authentication
Currently, the API operates without authentication. This will be updated in future versions.

## Async Operations
Long-running operations (like population generation) return job IDs for status polling.

 * OpenAPI spec version: 1.0.0
 */
import type {
  AddAgentTags200,
  AddName201,
  Agent,
  AgentMedia,
  AgentMediaResponse,
  AgentMediaReview200,
  AgentMediaReviewBody,
  AgentResponse,
  AgentsResponse,
  ApproveAgent200,
  ApproveAgentRequest,
  ApproveMedia200,
  ApproveMediaBody,
  AssignTagsInput,
  BatchAddNames201,
  BatchAddNamesBody,
  BatchCreateLocations201,
  BatchCreateLocationsRequest,
  BulkRejectMedia200,
  BulkRejectMediaBody,
  CreateAgentMediaRequest,
  CreateTag201,
  CreateTagInput,
  CreateUploadUrlRequest,
  CreateUploadUrlResponse,
  CreateWorldRequest,
  CropMediaRequest,
  CropMediaResponse,
  DeleteNames200,
  DeleteNamesParams,
  DeleteWorld200,
  ExtendAgentLock200,
  ExtendAgentLockBody,
  ExtendMediaReviewLock200,
  ExtendMediaReviewLockBody,
  GeneratePopulation202,
  GeneratePopulationRequest,
  GetAgentApprovedMedia200,
  GetAgentApprovedMediaParams,
  GetAgentCalendar200,
  GetAgentCalendarParams,
  GetAgentMediaParams,
  GetAgentPendingMedia200,
  GetAgentPendingMediaParams,
  GetAgentTags200,
  GetAgentsWithPendingMedia200,
  GetAgentsWithPendingMediaParams,
  GetConstants200,
  GetMediaReviewStats200,
  GetMediaReviewStatsParams,
  GetNextAgentForMediaReview200,
  GetNextAgentForMediaReviewParams,
  GetNextAgentForReview200,
  GetNextAgentForReviewParams,
  GetNextMediaForReview200,
  GetPopulationJob200,
  GetPopulationStats200,
  GetTag200,
  HealthResponse,
  InitializeWorld201,
  ListAgents200,
  ListAgentsParams,
  ListAllAgentsParams,
  ListTags200,
  ListWorlds200,
  NameEntry,
  ProcessVariantsRequest,
  RejectAgent200,
  RejectAgentRequest,
  RejectMedia200,
  RejectMediaBody,
  ReleaseMediaReviewLock200,
  ReviewStats,
  SetAgentTags200,
  SkipMediaReview200,
  SwitchAgentPrimaryMedia200,
  SwitchAgentPrimaryMediaParams,
  UnlockAgent200,
  UpdateAgent200,
  UpdateAgentMediaRequest,
  UpdateAgentRequest,
  UpdateDemographics200,
  UpdateDemographicsBody,
  UpdateTag200,
  UpdateTagInput,
  UpdateWorld200,
  UpdateWorldBody,
} from './schemas';

import { customInstance } from '../axios-instance';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Reports service readiness
 * @summary Basic health check
 */
export const getHealth = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<HealthResponse>({ url: `/health`, method: 'GET' }, options);
};

/**
 * Kubernetes liveness probe endpoint
 * @summary Liveness probe
 */
export const getLiveness = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<HealthResponse>({ url: `/health/live`, method: 'GET' }, options);
};

/**
 * Checks if database migrations are complete
 * @summary Startup probe
 */
export const getStartup = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<HealthResponse>({ url: `/health/startup`, method: 'GET' }, options);
};

/**
 * Returns system constants including review requirements.
Requires authentication and beta approval.

 * @summary Get system constants
 */
export const getConstants = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<GetConstants200>({ url: `/constants`, method: 'GET' }, options);
};

/**
 * List agents across all worlds with advanced search and filtering capabilities.
Supports pagination, search by name, and filtering by gender, age, status, and specific worlds.

 * @summary List all agents (global)
 */
export const listAllAgents = (
  params?: ListAllAgentsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AgentsResponse>({ url: `/api/agents`, method: 'GET', params }, options);
};

/**
 * Retrieve detailed information about a specific agent by ID, regardless of world
 * @summary Get agent details (global)
 */
export const getAgentById = (agentId: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<AgentResponse>(
    { url: `/api/agents/${agentId}/details`, method: 'GET' },
    options
  );
};

/**
 * Update agent properties like name, status, tags, or metadata
 * @summary Update agent (global)
 */
export const updateAgent = (
  agentId: string,
  updateAgentRequest: UpdateAgentRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateAgent200>(
    {
      url: `/api/agents/${agentId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: updateAgentRequest,
    },
    options
  );
};

/**
 * Get paginated list of media associated with an agent
 * @summary List agent media
 */
export const getAgentMedia = (
  agentId: string,
  params?: GetAgentMediaParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AgentMediaResponse>(
    { url: `/api/agents/${agentId}/media`, method: 'GET', params },
    options
  );
};

/**
 * Add new media to an agent
 * @summary Create agent media
 */
export const createAgentMedia = (
  agentId: string,
  createAgentMediaRequest: CreateAgentMediaRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AgentMedia>(
    {
      url: `/api/agents/${agentId}/media`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createAgentMediaRequest,
    },
    options
  );
};

/**
 * Update existing media associated with an agent
 * @summary Update agent media
 */
export const updateAgentMedia = (
  agentId: string,
  mediaId: string,
  updateAgentMediaRequest: UpdateAgentMediaRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AgentMedia>(
    {
      url: `/api/agents/${agentId}/media/${mediaId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: updateAgentMediaRequest,
    },
    options
  );
};

/**
 * Remove media from an agent (orphaned media records are automatically cleaned up)
 * @summary Delete agent media
 */
export const deleteAgentMedia = (
  agentId: string,
  mediaId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<void>(
    { url: `/api/agents/${agentId}/media/${mediaId}`, method: 'DELETE' },
    options
  );
};

/**
 * Switch an agent's primary avatar or banner to a different available media
 * @summary Set media as primary avatar or banner
 */
export const switchAgentPrimaryMedia = (
  agentId: string,
  mediaId: string,
  params: SwitchAgentPrimaryMediaParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<SwitchAgentPrimaryMedia200>(
    { url: `/api/agents/${agentId}/media/${mediaId}/set-primary`, method: 'PUT', params },
    options
  );
};

/**
 * Create a signed URL for direct media upload to storage
 * @summary Generate signed URL for media upload
 */
export const createAgentMediaUploadUrl = (
  agentId: string,
  createUploadUrlRequest: CreateUploadUrlRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateUploadUrlResponse>(
    {
      url: `/api/agents/${agentId}/media/upload-url`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createUploadUrlRequest,
    },
    options
  );
};

/**
 * Update media with new crop dimensions. Preserves the original and regenerates variants from the crop.
 * @summary Crop existing media
 */
export const cropAgentMedia = (
  agentId: string,
  mediaId: string,
  cropMediaRequest: CropMediaRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CropMediaResponse>(
    {
      url: `/api/agents/${agentId}/media/${mediaId}/crop`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: cropMediaRequest,
    },
    options
  );
};

/**
 * Remove crop from media and restore to original dimensions
 * @summary Reset media crop
 */
export const resetAgentMediaCrop = (
  agentId: string,
  mediaId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CropMediaResponse>(
    { url: `/api/agents/${agentId}/media/${mediaId}/crop`, method: 'DELETE' },
    options
  );
};

/**
 * Generate or regenerate media variants (blur, small, medium, large)
 * @summary Process media variants
 */
export const processAgentMediaVariants = (
  agentId: string,
  mediaId: string,
  processVariantsRequest?: ProcessVariantsRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AgentMedia>(
    {
      url: `/api/agents/${agentId}/media/${mediaId}/process-variants`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: processVariantsRequest,
    },
    options
  );
};

/**
 * Lock and retrieve the next pending agent for review with optional media filtering
 * @summary Get next agent for review
 */
export const getNextAgentForReview = (
  params?: GetNextAgentForReviewParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetNextAgentForReview200>(
    { url: `/api/review/next`, method: 'POST', params },
    options
  );
};

/**
 * Approve an agent
 * @summary Approve agent
 */
export const approveAgent = (
  agentId: string,
  approveAgentRequest: ApproveAgentRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ApproveAgent200>(
    {
      url: `/api/review/${agentId}/approve`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: approveAgentRequest,
    },
    options
  );
};

/**
 * Reject an agent with a reason
 * @summary Reject agent
 */
export const rejectAgent = (
  agentId: string,
  rejectAgentRequest: RejectAgentRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<RejectAgent200>(
    {
      url: `/api/review/${agentId}/reject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: rejectAgentRequest,
    },
    options
  );
};

/**
 * Release lock on an agent without approving or rejecting
 * @summary Unlock agent
 */
export const unlockAgent = (agentId: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<UnlockAgent200>(
    { url: `/api/review/${agentId}/unlock`, method: 'POST' },
    options
  );
};

/**
 * Extend the lock duration on an agent currently being reviewed
 * @summary Extend agent lock
 */
export const extendAgentLock = (
  agentId: string,
  extendAgentLockBody?: ExtendAgentLockBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ExtendAgentLock200>(
    {
      url: `/api/review/${agentId}/extend-lock`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: extendAgentLockBody,
    },
    options
  );
};

/**
 * Get statistics about the review queue and processed agents
 * @summary Get review statistics
 */
export const getReviewStats = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<ReviewStats>({ url: `/api/review/stats`, method: 'GET' }, options);
};

/**
 * Retrieves all worlds with statistics
 * @summary List all worlds
 */
export const listWorlds = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<ListWorlds200>({ url: `/worlds`, method: 'GET' }, options);
};

/**
 * Creates and initializes a new virtual world
 * @summary Create a new world
 */
export const initializeWorld = (
  createWorldRequest: CreateWorldRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<InitializeWorld201>(
    {
      url: `/worlds/initialize`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createWorldRequest,
    },
    options
  );
};

/**
 * Updates basic world properties like name and configuration
 * @summary Update world properties
 */
export const updateWorld = (
  worldId: string,
  updateWorldBody: UpdateWorldBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateWorld200>(
    {
      url: `/worlds/${worldId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: updateWorldBody,
    },
    options
  );
};

/**
 * Permanently deletes a world and all associated data (agents, locations, events, etc.)
 * @summary Delete a world
 */
export const deleteWorld = (worldId: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<DeleteWorld200>({ url: `/worlds/${worldId}`, method: 'DELETE' }, options);
};

/**
 * Updates demographic generation rules for a world
 * @summary Update demographic rules
 */
export const updateDemographics = (
  worldId: string,
  updateDemographicsBody: UpdateDemographicsBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateDemographics200>(
    {
      url: `/worlds/${worldId}/demographics`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: updateDemographicsBody,
    },
    options
  );
};

/**
 * List agents in a world with optional search and pagination
 * @summary List agents
 */
export const listAgents = (
  worldId: string,
  params?: ListAgentsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListAgents200>(
    { url: `/worlds/${worldId}/agents`, method: 'GET', params },
    options
  );
};

/**
 * Retrieves detailed information about a specific agent
 * @summary Get agent details
 */
export const getAgent = (
  worldId: string,
  agentId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<Agent>(
    { url: `/worlds/${worldId}/agents/${agentId}`, method: 'GET' },
    options
  );
};

/**
 * Retrieves an agent's calendar entries
 * @summary Get agent calendar
 */
export const getAgentCalendar = (
  worldId: string,
  agentId: string,
  params?: GetAgentCalendarParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetAgentCalendar200>(
    { url: `/worlds/${worldId}/agents/${agentId}/calendar`, method: 'GET', params },
    options
  );
};

/**
 * Initiates asynchronous batch generation of agents
 * @summary Generate population
 */
export const generatePopulation = (
  worldId: string,
  generatePopulationRequest: GeneratePopulationRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GeneratePopulation202>(
    {
      url: `/worlds/${worldId}/population/generate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: generatePopulationRequest,
    },
    options
  );
};

/**
 * Checks the status of a population generation job
 * @summary Get job status
 */
export const getPopulationJob = (
  worldId: string,
  jobId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetPopulationJob200>(
    { url: `/worlds/${worldId}/population/jobs/${jobId}`, method: 'GET' },
    options
  );
};

/**
 * Retrieves population statistics for a world
 * @summary Get population statistics
 */
export const getPopulationStats = (
  worldId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetPopulationStats200>(
    { url: `/worlds/${worldId}/population/stats`, method: 'GET' },
    options
  );
};

/**
 * Creates multiple locations with optional proximity connections
 * @summary Batch create locations
 */
export const batchCreateLocations = (
  worldId: string,
  batchCreateLocationsRequest: BatchCreateLocationsRequest,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<BatchCreateLocations201>(
    {
      url: `/worlds/${worldId}/locations/batch`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: batchCreateLocationsRequest,
    },
    options
  );
};

/**
 * Adds a single name to the world's name pool
 * @summary Add single name
 */
export const addName = (
  worldId: string,
  nameEntry: NameEntry,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AddName201>(
    {
      url: `/worlds/${worldId}/names`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: nameEntry,
    },
    options
  );
};

/**
 * Deletes names from the world's name pool based on filters
 * @summary Delete names
 */
export const deleteNames = (
  worldId: string,
  params?: DeleteNamesParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<DeleteNames200>(
    { url: `/worlds/${worldId}/names`, method: 'DELETE', params },
    options
  );
};

/**
 * Adds multiple names to the world's name pool
 * @summary Batch add names
 */
export const batchAddNames = (
  worldId: string,
  batchAddNamesBody: BatchAddNamesBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<BatchAddNames201>(
    {
      url: `/worlds/${worldId}/names/batch`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: batchAddNamesBody,
    },
    options
  );
};

/**
 * Retrieves all tags for the organization
 * @summary List all tags
 */
export const listTags = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<ListTags200>({ url: `/tags`, method: 'GET' }, options);
};

/**
 * Creates a new tag for the organization
 * @summary Create a new tag
 */
export const createTag = (
  createTagInput: CreateTagInput,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateTag201>(
    {
      url: `/tags`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createTagInput,
    },
    options
  );
};

/**
 * Retrieves a specific tag by ID
 * @summary Get a specific tag
 */
export const getTag = (tagId: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<GetTag200>({ url: `/tags/${tagId}`, method: 'GET' }, options);
};

/**
 * Updates an existing tag
 * @summary Update a tag
 */
export const updateTag = (
  tagId: string,
  updateTagInput: UpdateTagInput,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateTag200>(
    {
      url: `/tags/${tagId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: updateTagInput,
    },
    options
  );
};

/**
 * Deletes a tag (requires admin role)
 * @summary Delete a tag
 */
export const deleteTag = (tagId: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<void>({ url: `/tags/${tagId}`, method: 'DELETE' }, options);
};

/**
 * Retrieves all tags assigned to an agent
 * @summary Get agent tags
 */
export const getAgentTags = (agentId: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<GetAgentTags200>(
    { url: `/agents/${agentId}/tags`, method: 'GET' },
    options
  );
};

/**
 * Adds tags to an agent (keeps existing tags)
 * @summary Add tags to agent
 */
export const addAgentTags = (
  agentId: string,
  assignTagsInput: AssignTagsInput,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AddAgentTags200>(
    {
      url: `/agents/${agentId}/tags`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: assignTagsInput,
    },
    options
  );
};

/**
 * Sets the tags for an agent (replaces all existing tags)
 * @summary Set agent tags
 */
export const setAgentTags = (
  agentId: string,
  assignTagsInput: AssignTagsInput,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<SetAgentTags200>(
    {
      url: `/agents/${agentId}/tags`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: assignTagsInput,
    },
    options
  );
};

/**
 * Removes specific tags from an agent
 * @summary Remove tags from agent
 */
export const removeAgentTags = (
  agentId: string,
  assignTagsInput: AssignTagsInput,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<void>(
    {
      url: `/agents/${agentId}/tags`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: assignTagsInput,
    },
    options
  );
};

/**
 * Gets the next media item from the review queue for the authenticated reviewer.
If the reviewer already has a locked item, returns that item instead.
Items are locked for 30 minutes.
Excludes media from agents that are locked for review by other reviewers.

 * @summary Get next media for review
 */
export const getNextMediaForReview = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<GetNextMediaForReview200>(
    { url: `/api/media-review/next`, method: 'POST' },
    options
  );
};

/**
 * Locks the next agent with pending media for review by the authenticated reviewer.
If the reviewer already has an agent locked, returns that agent instead.
Agents are locked for 5 minutes to allow bulk review of all their pending media.
Returns the agent details along with all pending media items (including full media objects with variants).

 * @summary Get next agent for media review
 */
export const getNextAgentForMediaReview = (
  params?: GetNextAgentForMediaReviewParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetNextAgentForMediaReview200>(
    { url: `/api/media-review/next-agent`, method: 'POST', params },
    options
  );
};

/**
 * Approves a media item and removes it from the review queue
 * @summary Approve media
 */
export const approveMedia = (
  id: string,
  approveMediaBody: ApproveMediaBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ApproveMedia200>(
    {
      url: `/api/media-review/${id}/approve`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: approveMediaBody,
    },
    options
  );
};

/**
 * Rejects a media item and optionally deletes it
 * @summary Reject media
 */
export const rejectMedia = (
  id: string,
  rejectMediaBody: RejectMediaBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<RejectMedia200>(
    {
      url: `/api/media-review/${id}/reject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: rejectMediaBody,
    },
    options
  );
};

/**
 * Unlocks the media item without taking action, allowing another reviewer to pick it up
 * @summary Skip media review
 */
export const skipMediaReview = (id: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<SkipMediaReview200>(
    { url: `/api/media-review/${id}/skip`, method: 'POST' },
    options
  );
};

/**
 * Get statistics about the media review queue
 * @summary Get media review statistics
 */
export const getMediaReviewStats = (
  params?: GetMediaReviewStatsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetMediaReviewStats200>(
    { url: `/api/media-review/stats`, method: 'GET', params },
    options
  );
};

/**
 * Get a list of agents that have media pending review
 * @summary Get agents with pending media
 */
export const getAgentsWithPendingMedia = (
  params?: GetAgentsWithPendingMediaParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetAgentsWithPendingMedia200>(
    { url: `/api/media-review/agents-with-pending`, method: 'GET', params },
    options
  );
};

/**
 * Get all pending media items for a specific agent for bulk review.
Optionally lock items for review to prevent concurrent access.

 * @summary Get pending media for agent
 */
export const getAgentPendingMedia = (
  agentId: string,
  params?: GetAgentPendingMediaParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetAgentPendingMedia200>(
    { url: `/api/media-review/agents/${agentId}/pending`, method: 'GET', params },
    options
  );
};

/**
 * Get approved media items for a specific agent to use as reference
during bulk review of pending media.

 * @summary Get approved media for agent
 */
export const getAgentApprovedMedia = (
  agentId: string,
  params?: GetAgentApprovedMediaParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetAgentApprovedMedia200>(
    { url: `/api/media-review/agents/${agentId}/approved`, method: 'GET', params },
    options
  );
};

/**
 * Apply a single action to multiple media items for a specific agent.
Optionally delete specified media items atomically before processing.
If media deletion fails, the entire operation is rolled back.

 * @summary Process media review for agent
 */
export const agentMediaReview = (
  agentId: string,
  agentMediaReviewBody: AgentMediaReviewBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<AgentMediaReview200>(
    {
      url: `/api/media-review/agents/${agentId}/review`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: agentMediaReviewBody,
    },
    options
  );
};

/**
 * Extend the lock duration on an agent currently being reviewed for media
 * @summary Extend media review lock
 */
export const extendMediaReviewLock = (
  agentId: string,
  extendMediaReviewLockBody?: ExtendMediaReviewLockBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ExtendMediaReviewLock200>(
    {
      url: `/api/media-review/agents/${agentId}/extend-lock`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: extendMediaReviewLockBody,
    },
    options
  );
};

/**
 * Release the lock on an agent currently being reviewed for media
 * @summary Release media review lock
 */
export const releaseMediaReviewLock = (
  agentId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ReleaseMediaReviewLock200>(
    { url: `/api/media-review/agents/${agentId}/release-lock`, method: 'PUT' },
    options
  );
};

/**
 * Reject multiple media items at once
 * @summary Bulk reject media
 */
export const bulkRejectMedia = (
  bulkRejectMediaBody: BulkRejectMediaBody,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<BulkRejectMedia200>(
    {
      url: `/api/media-review/bulk-reject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: bulkRejectMediaBody,
    },
    options
  );
};

export type GetHealthResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>;
export type GetLivenessResult = NonNullable<Awaited<ReturnType<typeof getLiveness>>>;
export type GetStartupResult = NonNullable<Awaited<ReturnType<typeof getStartup>>>;
export type GetConstantsResult = NonNullable<Awaited<ReturnType<typeof getConstants>>>;
export type ListAllAgentsResult = NonNullable<Awaited<ReturnType<typeof listAllAgents>>>;
export type GetAgentByIdResult = NonNullable<Awaited<ReturnType<typeof getAgentById>>>;
export type UpdateAgentResult = NonNullable<Awaited<ReturnType<typeof updateAgent>>>;
export type GetAgentMediaResult = NonNullable<Awaited<ReturnType<typeof getAgentMedia>>>;
export type CreateAgentMediaResult = NonNullable<Awaited<ReturnType<typeof createAgentMedia>>>;
export type UpdateAgentMediaResult = NonNullable<Awaited<ReturnType<typeof updateAgentMedia>>>;
export type DeleteAgentMediaResult = NonNullable<Awaited<ReturnType<typeof deleteAgentMedia>>>;
export type SwitchAgentPrimaryMediaResult = NonNullable<
  Awaited<ReturnType<typeof switchAgentPrimaryMedia>>
>;
export type CreateAgentMediaUploadUrlResult = NonNullable<
  Awaited<ReturnType<typeof createAgentMediaUploadUrl>>
>;
export type CropAgentMediaResult = NonNullable<Awaited<ReturnType<typeof cropAgentMedia>>>;
export type ResetAgentMediaCropResult = NonNullable<
  Awaited<ReturnType<typeof resetAgentMediaCrop>>
>;
export type ProcessAgentMediaVariantsResult = NonNullable<
  Awaited<ReturnType<typeof processAgentMediaVariants>>
>;
export type GetNextAgentForReviewResult = NonNullable<
  Awaited<ReturnType<typeof getNextAgentForReview>>
>;
export type ApproveAgentResult = NonNullable<Awaited<ReturnType<typeof approveAgent>>>;
export type RejectAgentResult = NonNullable<Awaited<ReturnType<typeof rejectAgent>>>;
export type UnlockAgentResult = NonNullable<Awaited<ReturnType<typeof unlockAgent>>>;
export type ExtendAgentLockResult = NonNullable<Awaited<ReturnType<typeof extendAgentLock>>>;
export type GetReviewStatsResult = NonNullable<Awaited<ReturnType<typeof getReviewStats>>>;
export type ListWorldsResult = NonNullable<Awaited<ReturnType<typeof listWorlds>>>;
export type InitializeWorldResult = NonNullable<Awaited<ReturnType<typeof initializeWorld>>>;
export type UpdateWorldResult = NonNullable<Awaited<ReturnType<typeof updateWorld>>>;
export type DeleteWorldResult = NonNullable<Awaited<ReturnType<typeof deleteWorld>>>;
export type UpdateDemographicsResult = NonNullable<Awaited<ReturnType<typeof updateDemographics>>>;
export type ListAgentsResult = NonNullable<Awaited<ReturnType<typeof listAgents>>>;
export type GetAgentResult = NonNullable<Awaited<ReturnType<typeof getAgent>>>;
export type GetAgentCalendarResult = NonNullable<Awaited<ReturnType<typeof getAgentCalendar>>>;
export type GeneratePopulationResult = NonNullable<Awaited<ReturnType<typeof generatePopulation>>>;
export type GetPopulationJobResult = NonNullable<Awaited<ReturnType<typeof getPopulationJob>>>;
export type GetPopulationStatsResult = NonNullable<Awaited<ReturnType<typeof getPopulationStats>>>;
export type BatchCreateLocationsResult = NonNullable<
  Awaited<ReturnType<typeof batchCreateLocations>>
>;
export type AddNameResult = NonNullable<Awaited<ReturnType<typeof addName>>>;
export type DeleteNamesResult = NonNullable<Awaited<ReturnType<typeof deleteNames>>>;
export type BatchAddNamesResult = NonNullable<Awaited<ReturnType<typeof batchAddNames>>>;
export type ListTagsResult = NonNullable<Awaited<ReturnType<typeof listTags>>>;
export type CreateTagResult = NonNullable<Awaited<ReturnType<typeof createTag>>>;
export type GetTagResult = NonNullable<Awaited<ReturnType<typeof getTag>>>;
export type UpdateTagResult = NonNullable<Awaited<ReturnType<typeof updateTag>>>;
export type DeleteTagResult = NonNullable<Awaited<ReturnType<typeof deleteTag>>>;
export type GetAgentTagsResult = NonNullable<Awaited<ReturnType<typeof getAgentTags>>>;
export type AddAgentTagsResult = NonNullable<Awaited<ReturnType<typeof addAgentTags>>>;
export type SetAgentTagsResult = NonNullable<Awaited<ReturnType<typeof setAgentTags>>>;
export type RemoveAgentTagsResult = NonNullable<Awaited<ReturnType<typeof removeAgentTags>>>;
export type GetNextMediaForReviewResult = NonNullable<
  Awaited<ReturnType<typeof getNextMediaForReview>>
>;
export type GetNextAgentForMediaReviewResult = NonNullable<
  Awaited<ReturnType<typeof getNextAgentForMediaReview>>
>;
export type ApproveMediaResult = NonNullable<Awaited<ReturnType<typeof approveMedia>>>;
export type RejectMediaResult = NonNullable<Awaited<ReturnType<typeof rejectMedia>>>;
export type SkipMediaReviewResult = NonNullable<Awaited<ReturnType<typeof skipMediaReview>>>;
export type GetMediaReviewStatsResult = NonNullable<
  Awaited<ReturnType<typeof getMediaReviewStats>>
>;
export type GetAgentsWithPendingMediaResult = NonNullable<
  Awaited<ReturnType<typeof getAgentsWithPendingMedia>>
>;
export type GetAgentPendingMediaResult = NonNullable<
  Awaited<ReturnType<typeof getAgentPendingMedia>>
>;
export type GetAgentApprovedMediaResult = NonNullable<
  Awaited<ReturnType<typeof getAgentApprovedMedia>>
>;
export type AgentMediaReviewResult = NonNullable<Awaited<ReturnType<typeof agentMediaReview>>>;
export type ExtendMediaReviewLockResult = NonNullable<
  Awaited<ReturnType<typeof extendMediaReviewLock>>
>;
export type ReleaseMediaReviewLockResult = NonNullable<
  Awaited<ReturnType<typeof releaseMediaReviewLock>>
>;
export type BulkRejectMediaResult = NonNullable<Awaited<ReturnType<typeof bulkRejectMedia>>>;
